import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';

const MATERIAL_OPTIONS = [
  { value: 'pe_film',      label: 'PE Film (virgin)',         factor: 2.1,  recyclable: 'Yes (mono-PE)', epr: true },
  { value: 'pp',           label: 'PP (Polypropylene)',       factor: 1.95, recyclable: 'Yes (mono-PP)', epr: true },
  { value: 'pet',          label: 'PET Bottle/Film',          factor: 2.15, recyclable: 'Yes', epr: true },
  { value: 'mixed_plastic',label: 'Mixed Plastic',            factor: 2.3,  recyclable: 'No (contaminated)', epr: true },
  { value: 'cardboard',    label: 'Cardboard / Corrugated',   factor: 0.89, recyclable: 'Yes', epr: false },
  { value: 'glass',        label: 'Glass (bottle/jar)',       factor: 0.85, recyclable: 'Yes', epr: false },
];

const ALTERNATIVES = [
  { from: 'Mixed Plastic', to: 'Mono-PE Film', reduction: 40, desc: 'Eliminates contamination at recycling — full EPR credit eligibility' },
  { from: 'Virgin PE', to: 'Recycled-content PE (rPE)', reduction: 55, desc: '30-70% recycled content reduces cradle-to-gate CO2e' },
  { from: 'PE Film', to: 'Paper / FSC-certified cardboard', reduction: 58, desc: 'Renewable material, fully biodegradable, high recyclability' },
  { from: 'PET Bottle', to: 'Recycled PET (rPET)', reduction: 65, desc: 'rPET has 65% lower CO2e vs virgin PET' },
];

export default function GreenPackaging() {
  const [logs, setLogs] = useState([]);
  const [epr, setEpr] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({ activity_type: 'pe_film', quantity: '', supplier: '', period: '2025-Q4' });

  const selectedMaterial = MATERIAL_OPTIONS.find((m) => m.value === form.activity_type);
  const co2eEstimate = form.quantity && selectedMaterial ? Math.round(parseFloat(form.quantity) * selectedMaterial.factor / 10) / 100 : null;

  const load = () => {
    api.get('/services/logs?module=packaging').then((r) => setLogs(r.data.logs || [])).catch(() => {});
    api.get('/services/epr').then((r) => setEpr(r.data)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (!form.quantity) { setError('Quantity is required.'); return; }
    setSubmitting(true);
    try {
      await api.post('/services/logs', {
        module: 'packaging', activity_type: form.activity_type,
        quantity: parseFloat(form.quantity), unit: 'kg',
        supplier: form.supplier, period: form.period,
      });
      setSuccess('Packaging entry logged. EPR tracker has been updated.');
      setForm((f) => ({ ...f, quantity: '', supplier: '' }));
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to log packaging.');
    } finally { setSubmitting(false); }
  };

  const eprStatusColor = epr?.status === 'compliant' ? 'var(--neon-green)' : epr?.status === 'at_risk' ? 'var(--neon-yellow)' : 'var(--neon-pink)';

  return (
    <div className="page-shell page-stack">
      <section className="section-stack">
        <div className="section-tag">Green Services — Packaging</div>
        <h1>Circular Packaging</h1>
        <p className="section-copy">Track packaging material consumption, identify lower-carbon alternatives, and manage India CPCB EPR compliance.</p>
      </section>

      {/* EPR Status Banner */}
      {epr && (
        <section className="surface-panel glow-surface" style={{ padding: '1.25rem 1.5rem', borderLeft: `3px solid ${eprStatusColor}` }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '2rem', alignItems: 'center' }}>
            <div>
              <div className="stat-label">EPR compliance status</div>
              <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1rem', color: eprStatusColor, textTransform: 'uppercase', marginTop: '0.2rem' }}>
                {epr.status?.replace('_', ' ') || '—'}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem' }}>
              {[
                { label: 'Plastic consumed', value: `${epr.plastic_consumed_kg?.toLocaleString('en-IN') || 0} kg` },
                { label: 'EPR credits required', value: `${epr.required_credits_kg?.toLocaleString('en-IN') || 0} kg` },
                { label: 'Shortfall', value: `${epr.shortfall_kg?.toLocaleString('en-IN') || 0} kg`, color: epr.shortfall_kg > 0 ? 'var(--neon-pink)' : 'var(--neon-green)' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="stat-label">{s.label}</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.88rem', color: s.color || 'var(--text)', marginTop: '0.2rem' }}>{s.value}</div>
                </div>
              ))}
            </div>
            <Link to="/services/waste" className="btn btn-secondary btn-sm" style={{ whiteSpace: 'nowrap' }}>Log EPR recycling →</Link>
          </div>
        </section>
      )}

      {/* Log Form */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-tag" style={{ marginBottom: '1rem' }}>Log packaging usage</div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">Material type *</label>
              <select className="form-input" value={form.activity_type} onChange={(e) => setForm((f) => ({ ...f, activity_type: e.target.value }))}>
                {MATERIAL_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="form-label">Quantity used per month (kg) *</label>
              <input className="form-input" type="number" min="0" step="any" placeholder="e.g. 500" value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Supplier (optional)</label>
              <input className="form-input" type="text" placeholder="e.g. Shree Packaging Works" value={form.supplier} onChange={(e) => setForm((f) => ({ ...f, supplier: e.target.value }))} />
            </div>
            <div>
              <label className="form-label">Period</label>
              <select className="form-input" value={form.period} onChange={(e) => setForm((f) => ({ ...f, period: e.target.value }))}>
                {['2025-Q3', '2025-Q4', '2026-Q1', '2026-Q2'].map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {selectedMaterial && (
            <div style={{ padding: '0.75rem 1rem', background: 'rgba(255,100,50,0.07)', borderRadius: '8px', border: '1px solid rgba(255,100,50,0.2)', marginBottom: '1rem', display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
              <div><div className="stat-label">CO2e factor</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', color: 'var(--neon-orange)' }}>{selectedMaterial.factor} kg CO2e/kg</div></div>
              {co2eEstimate !== null && <div><div className="stat-label">Est. CO2e this entry</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.82rem', color: 'var(--neon-orange)' }}>{co2eEstimate} tCO2e</div></div>}
              <div><div className="stat-label">Recyclable</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: selectedMaterial.recyclable === 'No (contaminated)' ? 'var(--neon-pink)' : 'var(--neon-green)' }}>{selectedMaterial.recyclable}</div></div>
              <div><div className="stat-label">EPR-liable</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: selectedMaterial.epr ? 'var(--neon-yellow)' : 'var(--muted2)' }}>{selectedMaterial.epr ? 'Yes' : 'No'}</div></div>
            </div>
          )}

          {error && <div style={{ color: 'var(--neon-pink)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{error}</div>}
          {success && <div style={{ color: 'var(--neon-green)', fontFamily: 'Space Mono', fontSize: '0.72rem', marginBottom: '0.5rem' }}>{success}</div>}
          <button type="submit" className="btn btn-primary" disabled={submitting}>{submitting ? 'Logging…' : 'Log packaging'}</button>
        </form>
      </section>

      {/* Sustainable Alternatives */}
      <section className="surface-panel" style={{ padding: '1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
          <div className="section-tag">Design-for-Recycling (DfR) alternatives</div>
          <Link to="/vendors?category=packaging" className="btn btn-secondary btn-sm">Find packaging vendors →</Link>
        </div>
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          {ALTERNATIVES.map((a, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '1rem', alignItems: 'center', padding: '1rem 1.25rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)' }}>
              <div>
                <div className="stat-label" style={{ marginBottom: '0.25rem' }}>{a.from} → <span style={{ color: 'var(--neon-green)' }}>{a.to}</span></div>
                <div className="stat-sub">{a.desc}</div>
              </div>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                <div><div className="stat-label">CO2e reduction</div><div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.9rem', color: 'var(--neon-green)' }}>-{a.reduction}%</div></div>
              </div>
              <Link to="/vendors?category=packaging" className="btn btn-secondary btn-sm">Find vendor</Link>
            </div>
          ))}
        </div>
      </section>

      {/* Inventory */}
      {logs.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-tag" style={{ marginBottom: '1rem' }}>Packaging inventory log</div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Material</th><th>Quantity (kg)</th><th>Supplier</th><th>CO2e (tCO2e)</th></tr></thead>
            <tbody>
              {logs.slice(0, 10).map((l) => (
                <tr key={l.id}>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.7rem', color: 'var(--muted2)' }}>{new Date(l.created_at).toLocaleDateString('en-IN')}</td>
                  <td style={{ fontSize: '0.85rem' }}>{MATERIAL_OPTIONS.find((m) => m.value === l.activity_type)?.label || l.material_type}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem' }}>{l.quantity?.toLocaleString('en-IN')}</td>
                  <td style={{ fontSize: '0.78rem', color: 'var(--muted2)' }}>{l.supplier || '—'}</td>
                  <td style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.8rem', color: 'var(--neon-orange)' }}>{(l.co2e_emitted_kg / 1000).toFixed(3)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
